import { useEffect, useId, useRef, useState } from "react";

import { useListboxNav } from "../interaction";
import { Popover } from "./Popover";
import { cn } from "../lib/cn";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children" | "defaultValue"
> {
  /** null is "nothing chosen yet", which is what shows the placeholder. */
  value: string | null;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  /** Visible label. Prefer this over `aria-label`. */
  label?: string;
  /** Quiet helper line under the field. */
  hint?: string;
  /** Error message. Its presence puts the field in the invalid state. */
  error?: string;
  disabled?: boolean;
  id?: string;
  /** Extra classes for the trigger box, matching Input. */
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * The form-control select: an Input-shaped box that opens a listbox of options.
 * Keyboard follows the WAI-ARIA listbox pattern (arrows, Home/End, typeahead,
 * Enter/Space, Escape, and a Tab that closes on the way out) with the highlight
 * tracked by `aria-activedescendant`.
 *
 * `className` and `id` address the trigger box, matching Input; every other
 * native prop lands on the field root, the element that wraps label, trigger
 * and messages - so a test hook or a layout hook reaches the whole field
 * rather than one part of it.
 */
export function Select({
  value,
  onValueChange,
  options,
  placeholder = "Choose...",
  label,
  hint,
  error,
  disabled = false,
  id,
  className,
  ...rest
}: SelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const labelId = `${selectId}-label`;
  const hintId = `${selectId}-hint`;
  const errorId = `${selectId}-error`;
  const listboxId = `${selectId}-listbox`;
  const optionId = (index: number) => `${selectId}-option-${index}`;

  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") ||
    undefined;

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selectedOption =
    selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const firstEnabled = () => options.findIndex((option) => !option.disabled);
  const lastEnabled = () => {
    for (let index = options.length - 1; index >= 0; index -= 1) {
      if (!options[index].disabled) return index;
    }
    return -1;
  };

  const nav = useListboxNav({
    itemCount: options.length,
    getLabel: (index) => options[index]?.label ?? "",
    isDisabled: (index) => Boolean(options[index]?.disabled),
    onSelect: (index) => selectAt(index),
    onDismiss: () => closeList(),
    /* Tabbing away closes the list rather than leaving it open in the top layer
       behind the user, with the trigger still reading aria-expanded="true". */
    onTabOut: () => closeList(),
  });

  /** Opening lands on the current choice; `edge` only decides where to start without one. */
  const openList = (edge: "first" | "last") => {
    setOpen(true);
    const startAt =
      selectedOption && !selectedOption.disabled
        ? selectedIndex
        : edge === "first"
          ? firstEnabled()
          : lastEnabled();
    nav.setActiveIndex(startAt);
  };

  const closeList = () => {
    setOpen(false);
    nav.setActiveIndex(-1);
    triggerRef.current?.focus();
  };

  const selectAt = (index: number) => {
    const option = options[index];
    if (!option || option.disabled) return;
    closeList();
    onValueChange(option.value);
  };

  /* See Dropdown: light dismiss and the trigger's click ride the same press, so
     the press is where the real open state has to be read. */
  const openAtPressRef = useRef<boolean | null>(null);
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  });
  useEffect(() => {
    const element = triggerRef.current;
    if (!element) return;
    const handlePointerDown = () => {
      openAtPressRef.current = openRef.current;
    };
    element.addEventListener("pointerdown", handlePointerDown);
    return () => element.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  const handleTriggerClick = () => {
    const wasOpen = openAtPressRef.current ?? open;
    openAtPressRef.current = null;
    if (wasOpen) {
      setOpen(false);
      nav.setActiveIndex(-1);
      return;
    }
    openList("first");
  };

  const handleTriggerKeyDown: React.KeyboardEventHandler = (event) => {
    openAtPressRef.current = null;
    if (open) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openList("first");
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openList("last");
    }
  };

  useEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);

  return (
    <div {...rest} className="flex w-full flex-col gap-1.5">
      {label && (
        <label
          id={labelId}
          htmlFor={selectId}
          className="font-body text-sm font-medium text-ink"
        >
          {label}
        </label>
      )}
      <button
        type="button"
        id={selectId}
        ref={triggerRef}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        disabled={disabled}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-paper border bg-card px-3 py-2",
          "font-body text-sm",
          "transition duration-150 ease-paper motion-reduce:transition-none",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          "disabled:cursor-not-allowed disabled:bg-recess disabled:text-faint",
          error ? "border-ink-stopped" : "border-rule",
          className,
        )}
      >
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-left",
            selectedOption ? "text-ink" : "text-faint",
          )}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn(
            "size-4 shrink-0 text-annotation",
            "transition-transform duration-150 ease-paper motion-reduce:transition-none",
            open && "rotate-180",
          )}
        >
          <path d="M4 6.5 8 10.5 12 6.5" />
        </svg>
      </button>

      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) nav.setActiveIndex(-1);
        }}
        anchorRef={triggerRef}
        matchWidth
        id={listboxId}
        role="listbox"
        ref={listRef}
        tabIndex={-1}
        aria-labelledby={label ? labelId : undefined}
        aria-activedescendant={
          nav.activeIndex >= 0 ? optionId(nav.activeIndex) : undefined
        }
        onKeyDown={nav.handleKeyDown}
        /* The highlighted option is the focus indicator for the whole composite. */
        className="max-h-72 overflow-y-auto focus-visible:outline-none"
      >
        {options.map((option, index) => {
          const active = index === nav.activeIndex && !option.disabled;
          const selected = option.value === value;
          return (
            <div
              key={option.value}
              id={optionId(index)}
              role="option"
              aria-selected={selected}
              aria-disabled={option.disabled || undefined}
              onPointerEnter={() => {
                if (!option.disabled) nav.setActiveIndex(index);
              }}
              onClick={() => selectAt(index)}
              className={cn(
                "flex cursor-default items-center gap-2 rounded-[2px] px-3 py-2 text-sm select-none",
                "transition-colors duration-150 ease-paper motion-reduce:transition-none",
                option.disabled
                  ? "cursor-not-allowed text-faint"
                  : "text-ink-body",
                active && !option.disabled && "bg-recess text-ink",
                selected && "font-medium",
              )}
            >
              <span className="min-w-0 flex-1 truncate">{option.label}</span>
              {selected && (
                <svg
                  aria-hidden="true"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="size-4 shrink-0 text-accent-deep"
                >
                  <path d="M3 8.5 6.2 11.7 13 4.5" />
                </svg>
              )}
            </div>
          );
        })}
      </Popover>

      {hint && (
        <p id={hintId} className="font-body text-sm text-annotation">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="font-body text-sm text-ink-stopped">
          {error}
        </p>
      )}
    </div>
  );
}
